import { ContentBlocks } from "eddev/blocks"
import { defineView } from "eddev/views"
import { PageProvider } from "../contexts/PageContext"
import { HeaderCarousel } from "@components/site/HeaderCarousel"
import { HeaderBasic } from "@components/site/HeaderBasic"
import { FilmTile } from "@components/film/FilmTile"
import { filmsWithSessions } from "@hooks/useFilmSearch"
import { Fragment, useState } from "react"
import dayjs from "dayjs"
import { cn } from "@utils/tw"
import { Button } from "@components/ui/Button"
import { SiteFooter } from "@components/site/SiteFooter"

export default defineView("single-collection", (props) => {
  if (props.collection?.info?.hidden || !props.collection?.info?.public) {
    return null
  }

  const theme = props.collection?.info?.color || "yellow"
  const images = (props.collection?.films || [])
    .map((film) => ({ aspect: film?.media?.square?.aspect || 0, srcSet: film?.media?.square?.srcSet || "" }))
    .filter((f) => f.aspect !== 0 && f.srcSet !== "")

  const [mode, setMode] = useState<"a-z" | "date">("a-z")

  let films = filmsWithSessions(props.collection?.films)

  if (mode === "a-z") {
    films = films
      .sort((a, b) => a.film?.title!.localeCompare(b.film?.title!))
      .filter((film, ix, self) => ix === self.findIndex((t) => t.film?.title === film.film?.title))
  } else {
    films = films.sort((a, b) => dayjs.tz(a.session?.info?.dateTime).diff(dayjs.tz(b.session?.info?.dateTime)))
  }

  return (
    <PageProvider theme={theme}>
      <HeaderCarousel images={images} theme={theme} />
      <HeaderBasic title={props.collection?.title || ""} hasTopSpacing={false}>
        <div dangerouslySetInnerHTML={{ __html: props.collection?.info?.longDescription || "" }} />
      </HeaderBasic>

      <div className="flex flex-col gap-7 w-full mt-4">
        <div className="grid-auto grid-cols-1 md:grid-cols-2 gap-y-6 md:gap-y-5">
          <div className="col-span-1 md:col-span-2">
            <div className="flex items-center justify-between">
              <div className="flex gap-5 items-center">
                <p className="type-label-s-light">View by:</p>
                <div className="flex items-center gap-4">
                  <div
                    className={cn(
                      "type-label-l uppercase cursor-pointer relative hover-link",
                      "a-z" === mode && "hover-link-active",
                    )}
                    onClick={() => setMode("a-z")}
                  >
                    A-Z
                  </div>
                  <div
                    className={cn(
                      "type-label-l uppercase cursor-pointer relative hover-link",
                      "date" === mode && "hover-link-active",
                    )}
                    onClick={() => setMode("date")}
                  >
                    Date
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-end gap-4">
                <p className="type-label-s-light">{films.length} Films</p>
              </div>
            </div>
          </div>
        </div>
        <div className="grid-auto grid-cols-1 md:grid-cols-12 gap-y-6 md:gap-y-5">
          {films.map((item, ix) => {
            let previousDate = ix > 0 ? dayjs.tz(films[ix - 1].session?.info?.dateTime) : null
            let currentDate = dayjs.tz(item.session?.info?.dateTime)
            const newDay =
              ix === 0 || (previousDate && previousDate.format("YYYY-MM-DD") !== currentDate.format("YYYY-MM-DD"))
            return (
              <Fragment key={item.key}>
                {mode === "date" && newDay && (
                  <div className={cn("col-span-1 md:col-span-12", ix === 0 ? "mt-0" : "mt-7")}>
                    <h2 className="type-title-s uppercase">{currentDate.format("dddd MMMM D")}</h2>
                  </div>
                )}
                <div className="col-12 md:col-span-4 lg:col-span-3" key={item.key}>
                  <FilmTile film={item.film!} session={mode === "date" ? item.session : undefined} />
                </div>
              </Fragment>
            )
          })}
        </div>
      </div>
      <SiteFooter />
    </PageProvider>
  )
})
